import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import UnverifiedAccountActions from "@/components/UnverifiedAccountActions";

// Holding screen shown to a signed-in but unverified user. They land here
// after registering. The primary path is the 6-digit code from the email
// (scanner-proof — email-security bots can't type it); the magic link in the
// email still works as a fallback.
export default function CheckEmail() {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [verifyState, setVerifyState] = useState<"idle" | "verifying" | "error">("idle");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);

  const handleVerifyCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      setVerifyError("Enter the 6-digit code from your email.");
      return;
    }
    setVerifyState("verifying");
    setVerifyError(null);
    try {
      await apiRequest("POST", "/api/auth/verify-email-code", { code });
      // Verified — refetch the user so the gate advances to onboarding.
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    } catch (err) {
      setVerifyState("error");
      setVerifyError(
        err instanceof Error ? err.message : "That code didn't work. Try again.",
      );
    }
  };

  const handleResend = async () => {
    setResendState("sending");
    setError(null);
    try {
      await apiRequest("POST", "/api/auth/resend-verification", {});
      setResendState("sent");
    } catch (err) {
      setResendState("error");
      setError(
        err instanceof Error ? err.message : "Couldn't resend the email. Please try again.",
      );
    }
  };

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
  };

  return (
    <div className="min-h-screen bg-muted/20 py-10 px-4 flex flex-col items-center justify-center gap-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>Confirm your email</CardTitle>
          <CardDescription>
            We sent a 6-digit code to{" "}
            <span className="font-medium text-foreground">{user?.email || "your email"}</span>.
            Enter it below to finish setting up your account. Check your spam folder if you
            don't see it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="text-center text-2xl tracking-[0.5em]"
          />
          {verifyState === "error" && verifyError && (
            <p className="text-sm text-destructive">{verifyError}</p>
          )}
          {resendState === "sent" && (
            <p className="text-sm text-emerald-600">Sent! Check your inbox for a fresh code.</p>
          )}
          {resendState === "error" && error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-3 items-stretch">
          <Button onClick={handleVerifyCode} disabled={verifyState === "verifying"}>
            {verifyState === "verifying" ? "Verifying…" : "Verify"}
          </Button>
          <Button
            variant="outline"
            onClick={handleResend}
            disabled={resendState === "sending"}
          >
            {resendState === "sending" ? "Sending…" : "Resend code"}
          </Button>
          <Button variant="ghost" onClick={handleRefresh}>
            I've confirmed with the link — continue
          </Button>
        </CardFooter>
      </Card>
      <UnverifiedAccountActions />
    </div>
  );
}
